import { ORDER_SET_CURATION_RANK_URL, syncKey } from "@repo/api";
import type { ReorderOrderSetInput } from "@repo/api/schemas";
import { reorderOrderSetInputSchema } from "@repo/api/schemas";
import type { Collection } from "@tanstack/db";
import { defineWrite, type WriteOptions } from "../actions/define-write";
import type { RequestGroupRow } from "../fhir/types";

interface OrderSetCurationCollections {
  requestGroups: Collection<RequestGroupRow, string>;
}

interface OrderSetCurationActionDependencies {
  collections: () => OrderSetCurationCollections;
  persist: {
    reorder: (input: ReorderOrderSetInput, options: WriteOptions) => Promise<unknown>;
  };
}

function requireOrderSet(collection: Collection<RequestGroupRow, string>, orderSetId: string): RequestGroupRow {
  const row = collection.get(orderSetId);
  if (!row) throw new Error(`OrderSet '${orderSetId}' is not loaded`);
  return row;
}

export function curationExtensions(row: RequestGroupRow, curationRank: number) {
  const foreign = (row.extension ?? []).filter((extension) => extension.url !== ORDER_SET_CURATION_RANK_URL);
  return [...foreign, { url: ORDER_SET_CURATION_RANK_URL, valueInteger: curationRank }];
}

/** Internal testable factory for direct FHIR-row consequences of OrderSet curation intents. */
export function createOrderSetCurationActions(deps: OrderSetCurationActionDependencies) {
  const reorder = defineWrite<ReorderOrderSetInput>({
    optimistic: (params) => {
      const input = reorderOrderSetInputSchema.parse(params);
      const { requestGroups: rows } = deps.collections();
      const ranks = input.orderSetIds
        .map((id) => requireOrderSet(rows, id).curationRank ?? 0)
        .sort((left, right) => left - right);
      for (const [index, orderSetId] of input.orderSetIds.entries()) {
        const curationRank = ranks[index] ?? index;
        const row = requireOrderSet(rows, orderSetId);
        if (!row.carePlanId) throw new Error(`OrderSet '${orderSetId}' has no loaded care plan scope`);
        const carePlanId = row.carePlanId;
        rows.update(syncKey(row), (draft) => {
          draft.carePlanId = carePlanId;
          draft.curationRank = curationRank;
          draft.extension = curationExtensions(row, curationRank);
        });
      }
    },
    persist: async (input, options) => {
      await deps.persist.reorder(input, options);
    },
  });

  return { reorder };
}
